<template>
  <div class="web-panel-main-navigation" :class="{'-open': open }">
    <div class="darkoverlay" v-if="tabletLayout" @click="closeNavigation"></div>
    <div class="navmenu">
      <div class="stay-put-container">
        <div class="top-of-navigation">
          <div class="icon"></div>
          <div class="words"></div>
        </div>
        <nav class="navigation" :class="{'-subnav-open': isSubNavActive}">
          <ul class="navlist">
            <li
              class="navgroup"
              v-for="navigationGroup in navigationGroups"
              :key="navigationGroup.name"
            >
              <ul class="unstyled-list">
                <li
                  v-for="(navigationItem, i) in navigationGroup"
                  :key="navigationItem.name + i"
                  class="menuitem"
                  :class="{
              '-disable': navigationItem.disable
            }"
                >
                  <NavigationItem
                    :navigationItem="navigationItem"
                    :active="activeNavigationItem && activeNavigationItem === navigationItem"
                    @click="onClick(navigationItem)"
                  ></NavigationItem>
                </li>
              </ul>
            </li>
          </ul>
          <div class="subnavpanel">
            <template v-if="isSubNavActive">
              <h3 class="subtitle f-header white mb3">{{ activeNavigationItem.name }}</h3>
              <ul>
                <li
                  v-for="(child, i) in activeNavigationItem.children"
                  :key="child.name + i"
                  class="menuitem"
                  :class="{'-disable': child.disable}"
                >
                  <NavigationItem :navigationItem="child" @click="onClick(child)"></NavigationItem>
                </li>
              </ul>
            </template>
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import NavigationItem from './components/NavigationItem';
import { useWebPanel } from './hooks';
import useMQ from './hooks/mq';
import { setupClickOutside } from '@sbvr/common/legacy-shared/helpers/handleClickOutside';
import { watch, onUnmounted, ref, computed } from '@vue/composition-api';

export default {
  name: 'WebPanelNavigation',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Array,
      required: true,
    },
  },
  setup (props) {
    const { tabletLayout } = useMQ();
    const { isNavigationOpen } = useWebPanel();
    const closeNavigation = () => { isNavigationOpen.value = false; };
    // subnav
    function useSubNav () {
      const activeNavigationItem = ref(null);
      const { isNavigationOpen } = useWebPanel();
      let teardown;
      watch(async () => {
        if (activeNavigationItem.value?.children) {
          teardown && teardown();
          setImmediate(() => {
            teardown = setupClickOutside('.children-panel', () => {
              activeNavigationItem.value = null;
            });
          });
        }
      });
      onUnmounted(() => {
        teardown && teardown();
      });
      const isSubNavActive = computed(() => activeNavigationItem.value?.children);
      const onClick = (navigationItem) => {
        if (!navigationItem.children) isNavigationOpen.value = false;
        activeNavigationItem.value = navigationItem;
      };
      return {
        activeNavigationItem,
        isSubNavActive,
        onClick,
      };
    }
    const navigationGroups = computed(() => props.navigation.every(item => Array.isArray(item))
      ? props.navigation
      : [props.navigation]
    );
    return {
      closeNavigation,
      tabletLayout,
      navigationGroups,
      ...useSubNav(),
    };
  },
  components: {
    NavigationItem,
  },
};
</script>

<style scoped lang="postcss">
.web-panel-main-navigation {
  & .navmenu {
    position: sticky;
    top: 0;
    left: 0;
    height: 100vh;
    width: 85vw;
    max-width: 30rem;
    background-color: var(--colorDarkest);
    overflow: hidden;
    transition: transform 0.3s ease;
    transform: translateX(-100%) translateZ(0);
    will-change: transform;
    z-index: var(--zNavMenu);
  }

  & .top-of-navigation {
    position: relative;
    height: 6.7rem;
    display: flex;
    padding: var(--spacingXS) var(--spacingSm);
    background-color: rgba(0, 0, 0, 0.2);
    align-items: center;

    & .icon {
      flex: 0 0 2.5rem;
      height: 2.8rem;
      background-image: url(./assets/springboard-icon.svg);
      background-repeat: no-repeat;
      margin-right: var(--spacingSm);
    }

    & .words {
      flex: 1 0 auto;
      height: 3.2rem;
      background-image: url(./assets/springboard-logo.svg);
      background-repeat: no-repeat;
      background-position: -2.5rem center;
    }

    & .top-link {
      z-index: 3;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  & .navigation {
    z-index: 2;
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    padding-top: 67px;
    &.-subnav-open {
      z-index: 5 !important;
      & .navgroup {
        &:after {
          width: 3.2rem;
          margin: 0 1rem;
        }
      }
      & .subnavpanel {
        transform: translateX(0) translateZ(0);
        z-index: 2;
      }
    }
  }
  & .unstyled-list {
    padding-left: 0;
    margin: 0;
    list-style-type: none;
  }

  & .subtitle {
    padding: 0 var(--spacingSm);
  }

  & .navgroup {
    position: relative;
    &:after {
      content: "";
      transition: all 0.3s ease-in-out;
      margin: 0 1.5rem;
      position: absolute;
      top: 0;
      left: 0;
      width: calc(100% - 3rem);
      bottom: 0;
      pointer-events: none;
      border-bottom: solid 1px rgba(213, 216, 223, 0.25);
    }
  }
  & .navgroup:last-child {
    &:after {
      border-bottom: none;
    }
  }

  & .navlist {
    overflow-y: scroll !important;
    padding-left: 0;
    margin: 0;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    height: 100%;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      visibility: hidden;
      display: none;
    }
  }

  & .subnavpanel {
    position: absolute;
    background-color: var(--colorNearDark);
    right: 0;
    top: 0;
    bottom: 0;
    width: calc(100% - var(--spacingLg));
    padding: 2.2rem 0 0;
    transform: translateX(100%) translateZ(0);
    transition: transform 0.3s ease;
    will-change: transform;
  }
  & .menuitem.-disable {
    cursor: not-allowed;
    & > * {
      pointer-events: none;
    }
  }

  & .stay-put-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    transition: transform 0.3s ease;
    transform: translateX(0) translateZ(0);
    will-change: transform;
  }

  & .darkoverlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 399;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
    will-change: opacity;
    transform: translateZ(0);
    z-index: var(--zDarkenOverlay);
  }
  &.-open {
    & .navmenu {
      transform: translateX(0) translateZ(0);
    }
    & .darkoverlay {
      opacity: 1;
      pointer-events: auto;
    }
  }
}
</style>
